import Service, { service } from '@ember/service'
import { AMER } from 'district-ui-client/domain/marketing-regions'
import type SessionService from 'district-ui-client/services/session'
import type FeaturesService from 'district-ui-client/services/features'
import type ActiveRouteService from 'district-ui-client/services/active-route'
import { SubscriptionType } from 'district-ui-client/domain/subscription-type'

/**
 * A directory of feature access names / ids.
 *
 * Intentionally separate to products, as a feature access may be restricted to part of a product, or extend across
 * multiple.
 * Intentionally separate to feature flag names as we use them in conjunction with other logic to determine access. Also
 * feature access may be solely determined by logic other than flags.
 *
 * They're named to describe the feature being restricted. These are intended to be longer-lived than feature flags.
 */
export const FeatureAccess = {
  TestFeature: 'test-feature', // for testing, not a real feature
  ManageSection: 'manage-section',
  ManageSchools: 'manage-schools',
  ManageTeachers: 'manage-teachers',
  ManageStudents: 'manage-students',
} as const

export type FeatureAccessId = (typeof FeatureAccess)[keyof typeof FeatureAccess]

/**
 * A service to help handle feature access. Intended to handle feature access logic regarding;
 * - countries/regions
 * - partners
 * - feature flags (runtime toggling of behaviour)
 *
 * Works similarly to feature flags service but takes into account other aspects that might impact whether a feature is
 * accessible to a user, allowing us to;
 * - maintain access logic on partially released features (eg a region-based rollout)
 * - expand to more regions by toggling a feature flag (eg released to AU, but FF toggle gives worldwide access)
 * - use a single feature access lock across multiple feature flags (where it makes sense to do so)
 * - have clearly defined & tested rules for access
 *
 * With this service, we keep feature flags as a simple, short-lived, runtime behaviour toggle.
 *
 * There is also a companion helper available {{#if (can-access "standards")}} ....
 *
 * @example <caption>partially released feature to single region</caption>
 * ```
 * // canAccess logic
 * case 'standards':
 *   return AMER.includes(countryCode)
 *
 * // usage
 * get showStandardsPerformanceReport() {
 *   return this.featureControl.canAccess('standards')
 * }
 * ```
 *
 * @example <caption>expand access to more regions with a feature flag</caption>
 * // canAccess logic
 * case 'standards':
 *   if (this.features.isEnabled('dui_standards_anz')) {
 *     return AMER.includes(countryCode) || ANZ.includes(countryCode)
 *   }
 *   return AMER.includes(countryCode)
 *
 * // usage
 * get showStandardsPerformanceReport() {
 *   return this.featureControl.canAccess('standards')
 * }
 */
export default class FeatureControlService extends Service {
  @service session!: SessionService

  @service features!: FeaturesService

  @service activeRoute!: ActiveRouteService

  canAccess(featureId: FeatureAccessId): boolean {
    const countryCode = this.session.currentDisco.countryCode
    switch (featureId) {
      case FeatureAccess.TestFeature:
        return AMER.includes(countryCode)
      case FeatureAccess.ManageSection:
        return this.canAccessManageSection
      case FeatureAccess.ManageSchools:
        return this.canAccessManageSchools
      case FeatureAccess.ManageTeachers:
        return this.canAccessManageTeachers
      case FeatureAccess.ManageStudents:
        return this.canAccessManageStudents
      default:
        return false
    }
  }

  private get district() {
    return this.session.currentDistrict
  }

  private get subscriptionType() {
    return this.activeRoute.subscriptionType
  }

  private get canAccessManageSection(): boolean {
    return (
      this.subscriptionType !== SubscriptionType.Mathletics ||
      this.oneRosterMxCanAccessManage ||
      this.features.isEnabled('dui_mx_non_one_roster_manage_schools')
    )
  }

  private get canAccessManageSchools(): boolean {
    return (
      this.subscriptionType !== SubscriptionType.Mathletics ||
      this.oneRosterMxCanAccessManage ||
      this.features.isEnabled('dui_mx_non_one_roster_manage_schools')
    )
  }

  private get canAccessManageTeachers(): boolean {
    return this.subscriptionType !== SubscriptionType.Mathletics || this.oneRosterMxCanAccessManage
  }

  private get canAccessManageStudents(): boolean {
    return this.subscriptionType !== SubscriptionType.Mathletics || this.oneRosterMxCanAccessManage
  }

  private get oneRosterMxCanAccessManage(): boolean {
    const { district } = this
    const mxManageEnabled = this.features.isEnabled('dui_mx_manage')
    return Boolean(district.oneRosterEnabled && mxManageEnabled)
  }
}

declare module '@ember/service' {
  interface Registry {
    'feature-control': FeatureControlService
  }
}
